import React from "react";
import { Box, Typography } from "@mui/material";
import {
  CalendarMonthOutlined as CalendarIcon,
  DashboardOutlined as DashboardIcon,
  FavoriteBorder as HeartIcon,
  HomeOutlined as HomeIcon,
  LocalHospitalOutlined as ClinicIcon,
  PeopleOutline as PeopleIcon,
  PersonOutline as PersonIcon,
  SmartphoneOutlined as PhoneIcon,
  WaterDropOutlined as WaterIcon,
} from "@mui/icons-material";
import { COLORS } from "../../styles/uiConstants";
import { generatePalette, setAlpha } from "../../theme";

/**
 * Mobil uygulamanın (Pupill-Mobil/utils/theme.ts) RGB tabanlı açma formülü — web'in HSL
 * `lighten`'ı biraz farklı ton verir; önizleme telefondakiyle birebir aynı olsun.
 */
function mobileLighten(hex, amt) {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v) => Math.max(0, Math.min(255, Math.round(v + (255 - v) * amt)));
  const r = ch((n >> 16) & 255);
  const g = ch((n >> 8) & 255);
  const b = ch(n & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Klinik markasının (tema rengi + logolar) web paneli ve hasta mobil uygulamasında
 * nasıl görüneceğini gösteren canlı önizleme. Kaydetmeden önce seçilen renk/logo ile
 * anında güncellenir; veri çekmez. Mobil taraf, uygulamadaki kurallarla aynıdır:
 * header gradient = [renk %22 açık → renk], logolar HER ZAMAN beyaz zeminde.
 */
export default function ClinicBrandPreview({ themeColor, logoSrc, iconSrc, clinicName }) {
  const p = generatePalette(themeColor || undefined);
  const name = clinicName || "Kliniğiniz";

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 260px" }, gap: 3, alignItems: "start" }}>
      <Box>
        <Caption icon={<DashboardIcon sx={{ fontSize: 16 }} />} text="Web paneli" />
        <WebMock p={p} name={name} logoSrc={logoSrc} iconSrc={iconSrc} />
      </Box>
      <Box sx={{ justifySelf: { xs: "center", md: "stretch" } }}>
        <Caption icon={<PhoneIcon sx={{ fontSize: 16 }} />} text="Hasta mobil uygulaması" />
        <PhoneMock p={p} name={name} iconSrc={iconSrc || logoSrc} />
      </Box>
    </Box>
  );
}

function Caption({ icon, text }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1, color: COLORS.slate500 }}>
      {icon}
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>{text}</Typography>
    </Box>
  );
}

/** Logo her zaman beyaz zeminde (şeffaf PNG'ler renkli yüzeyde kaybolmasın). */
function LogoBox({ src, size, wide = false, round = false, fallback }) {
  return (
    <Box
      sx={{
        width: wide ? "100%" : size,
        height: size,
        borderRadius: round ? "50%" : 1.5,
        bgcolor: COLORS.white,
        border: `1px solid ${COLORS.slate200}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {src ? (
        <img src={src} alt="" style={{ maxWidth: "88%", maxHeight: "80%", objectFit: "contain" }} />
      ) : (
        fallback
      )}
    </Box>
  );
}

function WebMock({ p, name, logoSrc, iconSrc }) {
  const nav = [
    { icon: <DashboardIcon sx={{ fontSize: 15 }} />, label: "Dashboard", active: true },
    { icon: <PeopleIcon sx={{ fontSize: 15 }} />, label: "Hastalar" },
    { icon: <CalendarIcon sx={{ fontSize: 15 }} />, label: "Randevular" },
  ];
  return (
    <Box
      aria-label="Web paneli önizlemesi"
      role="img"
      sx={{ border: `1px solid ${COLORS.slate200}`, borderRadius: 2, overflow: "hidden", bgcolor: COLORS.white, boxShadow: "0 1px 2px rgba(15,23,42,0.06)" }}
    >
      {/* tarayıcı çubuğu */}
      <Box sx={{ display: "flex", gap: 0.5, px: 1.25, py: 0.75, bgcolor: COLORS.slate100, borderBottom: `1px solid ${COLORS.slate200}` }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: COLORS.slate300 }} />
        ))}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "150px minmax(0, 1fr)", minHeight: 220 }}>
        {/* kenar çubuğu */}
        <Box sx={{ borderRight: `1px solid ${COLORS.slate100}`, p: 1.25, display: "flex", flexDirection: "column", gap: 0.75 }}>
          <LogoBox
            src={logoSrc || iconSrc}
            size={40}
            wide
            fallback={<Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: p.primary }}>{name}</Typography>}
          />
          {nav.map((n) => (
            <Box
              key={n.label}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1,
                py: 0.6,
                borderRadius: 1.5,
                fontSize: "0.7rem",
                fontWeight: n.active ? 700 : 500,
                color: n.active ? p.primary : COLORS.slate600,
                bgcolor: n.active ? setAlpha(p.primary, 0.1) : "transparent",
                border: `1px solid ${n.active ? setAlpha(p.primary, 0.2) : "transparent"}`,
              }}
            >
              {n.icon}
              {n.label}
            </Box>
          ))}
        </Box>
        {/* içerik */}
        <Box sx={{ bgcolor: COLORS.slate50, p: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: COLORS.slate800 }}>Dashboard</Typography>
          <Box sx={{ bgcolor: COLORS.white, border: `1px solid ${COLORS.slate200}`, borderRadius: 1.5, p: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: 1, background: p.backgroundGradient, display: "flex", alignItems: "center", justifyContent: "center", color: p.contrastText }}>
                <PeopleIcon sx={{ fontSize: 14 }} />
              </Box>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: COLORS.slate700 }}>Bugünkü hastalar</Typography>
            </Box>
            <Box sx={{ height: 6, borderRadius: 3, bgcolor: COLORS.slate100, overflow: "hidden" }}>
              <Box sx={{ width: "62%", height: "100%", bgcolor: p.primary }} />
            </Box>
            <Box sx={{ alignSelf: "flex-end", px: 1.25, py: 0.5, borderRadius: 1.5, bgcolor: p.buttonPrimary, color: p.contrastText, fontSize: "0.68rem", fontWeight: 700 }}>
              Kaydet
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function PhoneMock({ p, name, iconSrc }) {
  const g1 = mobileLighten(p.primary, 0.22);
  const tint = mobileLighten(p.primary, 0.86);
  return (
    <Box
      aria-label="Mobil uygulama önizlemesi"
      role="img"
      sx={{
        width: 240,
        mx: { xs: "auto", md: 0 },
        border: `8px solid ${COLORS.slate800}`,
        borderRadius: 5,
        overflow: "hidden",
        bgcolor: COLORS.slate50,
        boxShadow: "0 6px 20px rgba(15,23,42,0.12)",
      }}
    >
      {/* başlık — uygulamadaki AppHeader gibi gradient + beyaz zeminli logo */}
      <Box sx={{ background: `linear-gradient(135deg, ${g1}, ${p.primary})`, px: 1.5, pt: 2, pb: 1.25, borderRadius: "0 0 16px 16px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.62rem", color: setAlpha(COLORS.white, 0.85) }}>Merhaba,</Typography>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: COLORS.white }} noWrap>
              Ayşe Yılmaz
            </Typography>
          </Box>
          <HeartIcon sx={{ fontSize: 22, color: COLORS.white }} />
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 0 }}>
            <LogoBox src={iconSrc} size={34} round fallback={<ClinicIcon sx={{ fontSize: 18, color: p.primary }} />} />
            <Typography sx={{ fontSize: "0.55rem", color: COLORS.white, mt: 0.25, maxWidth: 80 }} noWrap>
              {name}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* içerik — Bugün kartı */}
      <Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: COLORS.slate800 }}>Bugün</Typography>
        <Box sx={{ bgcolor: COLORS.white, borderRadius: 2, p: 1.25, display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "0.6rem", color: COLORS.slate500 }}>İlaçlar</Typography>
            <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: COLORS.slate600 }}>1/2 alındı</Typography>
          </Box>
          <Box sx={{ height: 5, borderRadius: 3, bgcolor: COLORS.slate100, overflow: "hidden" }}>
            <Box sx={{ width: "50%", height: "100%", bgcolor: p.primary }} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 900, color: COLORS.slate800 }}>21:00</Typography>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: COLORS.slate700, flex: 1 }} noWrap>
              Parol · 1 tablet
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Box sx={{ flex: 1, textAlign: "center", py: 0.5, borderRadius: 1.5, border: `1px solid ${COLORS.slate300}`, fontSize: "0.62rem", fontWeight: 700, color: COLORS.slate600 }}>
              Atla
            </Box>
            <Box sx={{ flex: 1, textAlign: "center", py: 0.5, borderRadius: 1.5, bgcolor: p.primary, fontSize: "0.62rem", fontWeight: 700, color: COLORS.white }}>
              Aldım
            </Box>
          </Box>
        </Box>
        <Box sx={{ bgcolor: COLORS.white, borderRadius: 2, p: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: tint, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <WaterIcon sx={{ fontSize: 15, color: p.primary }} />
          </Box>
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: COLORS.slate700 }}>1,25 / 2 L</Typography>
        </Box>
      </Box>
      {/* alt menü */}
      <Box sx={{ display: "flex", justifyContent: "space-around", alignItems: "center", bgcolor: COLORS.white, py: 0.75, borderRadius: "14px 14px 0 0" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.4, borderRadius: 2, bgcolor: mobileLighten(p.primary, 0.85), color: p.primary }}>
          <HomeIcon sx={{ fontSize: 15 }} />
          <Typography sx={{ fontSize: "0.6rem", fontWeight: 700 }}>Ana</Typography>
        </Box>
        <HeartIcon sx={{ fontSize: 15, color: COLORS.slate400 }} />
        <PhoneIcon sx={{ fontSize: 15, color: COLORS.slate400 }} />
        <PersonIcon sx={{ fontSize: 15, color: COLORS.slate400 }} />
      </Box>
    </Box>
  );
}
